import React from 'react';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Plane, Hotel as HotelIcon, Car, Utensils, Sparkles } from 'lucide-react';
import type { BudgetCategory } from '@/services/supabase';

interface BudgetCategoriesTableProps {
  categories: BudgetCategory[];
}

const DEFAULT_CATEGORIES = [
  { name: 'Flights', icon: Plane },
  { name: 'Accommodation', icon: HotelIcon },
  { name: 'Transportation', icon: Car },
  { name: 'Food', icon: Utensils },
  { name: 'Extras', icon: Sparkles },
];

const getCategoryIcon = (categoryName: string) => {
  const defaultCategory = DEFAULT_CATEGORIES.find(
    cat => cat.name.toLowerCase() === categoryName.toLowerCase()
  );
  return defaultCategory?.icon || Sparkles;
};

export const BudgetCategoriesTable: React.FC<BudgetCategoriesTableProps> = ({ categories }) => {
  // If no categories exist, show default categories with 0 values
  const displayCategories = categories.length === 0
    ? DEFAULT_CATEGORIES.map(cat => ({
        id: cat.name.toLowerCase(),
        category_name: cat.name,
        allocated_amount: 0,
        spent_amount: 0,
        currency: 'USD',
      }))
    : categories.map(cat => ({
        ...cat,
        spent_amount: 0, // TODO: Get actual spent amount from expenses
      }));

  return (
    <div className="border rounded-lg">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[200px]">Category</TableHead>
            <TableHead>Allocated Amount</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {displayCategories.map((category) => {
            const Icon = getCategoryIcon(category.category_name);
            const allocatedAmount = category.allocated_amount || 0;
            const spentAmount = category.spent_amount || 0;
            const spentPercentage = allocatedAmount > 0 ? (spentAmount / allocatedAmount) * 100 : 0;

            return (
              <TableRow key={category.id}>
                <TableCell className="font-medium">
                  <div className="flex items-center gap-2">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                    {category.category_name}
                  </div>
                </TableCell>
                <TableCell>
                  <div className="flex items-center">
                    <div className="w-3/4">
                      <div className="relative w-full h-8 bg-gray-200 rounded-md overflow-hidden">
                        {/* Progress bar - will show spending in the future */}
                        <div
                          className="absolute inset-y-0 left-0 bg-primary transition-all"
                          style={{ width: `${Math.min(spentPercentage, 100)}%` }}
                        />
                        {/* Allocated amount text */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <span className="text-sm font-semibold text-gray-700">
                            ${allocatedAmount.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};
